---
title: Network and egress
lede: A micro-VM session reaches the hosts on its allowlist and nothing else.
order: 39
part: Shape the machine
---

Egress is denied by default: a micro-VM session reaches only the hosts its allowlist names. Manage the list in **Settings › Network**, and add hosts for one workspace in **Workspace settings › Network**.

![Network settings](media/settings-network.webp)

A session placed on this computer uses your computer's network, with nothing denied or asked.

### Always allowed

The agents' endpoints, the package registries (npm, Yarn, PyPI, Maven, Gradle, Go, crates.io, Packagist), GitHub and its content hosts, and the Debian and Alpine mirrors. The page lists them under **Always allowed**; you cannot remove them. An entry starting `*.` covers that name and every name under it. A workspace with [Docker in the VM](/docker) also gets Docker Hub and Ubuntu's package hosts.

### Add a host

Type a bare hostname (no scheme, port or wildcard) and add it. In **Settings › Network** it goes under **Additional hosts** and is open to every workspace. On a workspace's page it goes under **This workspace adds** and is open to that workspace only. Each list holds up to 64.

![A workspace's network page](media/ws-network.webp)

:::warn Every host is a door
The allowlist is what stops repository or dependency content from steering an agent into sending your code elsewhere. Add only what a session needs.
:::

### When a session hits an unlisted host

A VM's network is sealed when it boots. When a session is refused a host that exists, Airlock asks you:

- **Allow** adds the host to the workspace's list and moves the session to a new machine on the same disk; the stage says so while it starts, usually a few seconds. Files, changes and the agents' conversations are kept, and an agent that was working carries on. It opens ports 80 and 443 of that one name. If the new machine cannot start with the host, the session comes back on the network it had, and a message says the host is still refused.
- **Keep blocked** leaves it refused for this session.

![The prompt for a refused host](media/egress-machine-prompt.webp)

**When a session reaches a host nobody listed** (**Unlisted hosts** on a workspace's page) offers **Ask, holding the call** (the default), **Allow automatically** and **Block — allowlist only**. On a micro-VM session, **Ask** and **Allow** raise the prompt above for a refused host; **Block** raises nothing, and the allowlist is the whole answer. Discovery (below) is the one run where **Ask** holds the call itself. Claude Code's own telemetry and error-reporting hosts stay refused, and nobody is asked about them.

If your organization sets a network policy, both Network pages show it under **Set by** your organization: hosts it allows in every workspace, hosts it blocks everywhere (with every name under them — no list can allow one, and a refused one is never put to you as a question), and the mode it holds, if any.

### Discover the hosts a workspace needs

On a workspace's Network page, **Discover hosts** installs its repositories in a sandbox and asks you about every host nothing allows yet. What you allow lands in **This workspace adds**. Once it has run, the button reads **Re-run discovery**.

- **Already decided for this workspace** lists answers not yet on a list: **Allow**, **Block**, or **Keep all**.
- **Never asked about** holds the hosts you blocked, so a retry does not ask again. **Ask again** removes one.

### Resolvers

**Resolvers**, in **Settings › Network**, turn hostnames into addresses, asked in order. Each is an IPv4 address, up to 6; put your network's own first if it permits only those. Left empty, sandboxes use `1.1.1.1` and `8.8.8.8`.

Changes to the lists and resolvers apply to sessions started afterwards.
